<div class="professional-services-config">
  <div class="service-header mb-3">
    <h3 class="service-title">
      <kendo-svg-icon
        [icon]="securityCheckIcon"
        class="me-2"
      ></kendo-svg-icon>
      Professional Services Configuration
    </h3>
    <p class="service-subtitle text-muted">Select and configure professional security services</p>
  </div>

  <form
    [formGroup]="professionalServicesForm"
    class="row g-3"
  >
    <!-- Service Selection Section -->
    <div class="col-12">
      <div class="config-section">
        <h5 class="section-title mb-3">
          <kendo-svg-icon
            [icon]="gearIcon"
            class="me-2"
          ></kendo-svg-icon>
          Professional Services
        </h5>

        <!-- Service Selection Cards -->
        <div class="row g-3">
          @for (service of professionalServiceOptions; track service.value) {
            <div class="col-12 col-sm-6 col-lg-4 col-xl">
              <div
                class="service-option-card"
                [class.selected]="isServiceSelected(service.value)"
                (click)="toggleService(service.value)"
                style="cursor: pointer"
              >
                <div class="service-option-header">
                  <kendo-svg-icon
                    [icon]="getServiceIcon(service.value)"
                    class="service-icon"
                  ></kendo-svg-icon>
                  <h6 class="service-name">{{ service.text }}</h6>
                </div>
                <p class="service-description">{{ service.description }}</p>
                <div class="selection-indicator">
                  @if (isServiceSelected(service.value)) {
                    <kendo-svg-icon
                      [icon]="infoCircleIcon"
                      class="selected-icon"
                    ></kendo-svg-icon>
                    <span class="selected-text">Selected</span>
                  }
                </div>
              </div>
            </div>
          }
        </div>

        <!-- Penetration Testing Configuration -->
        @if (showPenetrationTestingFields) {
          <div class="col-12 mt-4">
            <div class="config-section">
              <h5 class="section-title mb-3">
                <kendo-svg-icon
                  [icon]="bugIcon"
                  class="me-2"
                ></kendo-svg-icon>
                Penetration Testing Configuration
              </h5>
              <div class="row g-3">
                <!-- Penetration Testing Types -->
                <div class="col-md-6">
                  <kendo-formfield>
                    <kendo-label
                      [for]="pentestTypesSelect"
                      text="Testing Types *"
                    ></kendo-label>
                    <kendo-multiselect
                      formControlName="pentestTypes"
                      #pentestTypesSelect
                      [data]="pentestTypeOptions"
                      textField="text"
                      valueField="value"
                      placeholder="Select testing types"
                      class="w-100"
                      (valueChange)="onPentestTypesChange()"
                    ></kendo-multiselect>
                    <kendo-formhint>
                      <kendo-svg-icon
                        [icon]="infoCircleIcon"
                        class="me-1"
                      ></kendo-svg-icon>
                      Select the types of penetration testing required
                    </kendo-formhint>
                    @if (
                      professionalServicesForm.get('pentestTypes')?.invalid &&
                      professionalServicesForm.get('pentestTypes')?.touched
                    ) {
                      <kendo-formerror>
                        <kendo-svg-icon
                          [icon]="exclamationTriangleIcon"
                          class="me-1"
                        ></kendo-svg-icon>
                        Please select at least one testing type
                      </kendo-formerror>
                    }
                  </kendo-formfield>
                </div>

                <!-- Number of Subnets -->
                <div class="col-md-6">
                  <kendo-formfield>
                    <kendo-label
                      [for]="subnetCount"
                      text="Number of Subnets *"
                    ></kendo-label>
                    <kendo-numerictextbox
                      formControlName="subnetCount"
                      #subnetCount
                      [min]="1"
                      placeholder="Enter number of subnets"
                      class="w-100"
                      (valueChange)="reapplyValidation()"
                    ></kendo-numerictextbox>
                    <kendo-formhint>
                      <kendo-svg-icon
                        [icon]="infoCircleIcon"
                        class="me-1"
                      ></kendo-svg-icon>
                      Total number of subnets to be tested
                    </kendo-formhint>
                    @if (
                      professionalServicesForm.get('subnetCount')?.invalid &&
                      professionalServicesForm.get('subnetCount')?.touched
                    ) {
                      <kendo-formerror>
                        <kendo-svg-icon
                          [icon]="exclamationTriangleIcon"
                          class="me-1"
                        ></kendo-svg-icon>
                        Number of subnets is required and must be at least 1
                      </kendo-formerror>
                    }
                  </kendo-formfield>
                </div>

                <!-- WebApp Domains (conditional) -->
                @if (showWebappDomains) {
                  <div class="col-12">
                    <kendo-formfield>
                      <kendo-label
                        [for]="domains"
                        text="Web Application Domains"
                      ></kendo-label>
                      <kendo-textarea
                        formControlName="domains"
                        #domains
                        placeholder="Enter domains to test (one per line)"
                        [rows]="3"
                        class="w-100"
                      ></kendo-textarea>
                      <kendo-formhint>
                        <kendo-svg-icon
                          [icon]="infoCircleIcon"
                          class="me-1"
                        ></kendo-svg-icon>
                        List of web application domains to be tested
                      </kendo-formhint>
                    </kendo-formfield>
                  </div>
                }

                <!-- Start Date -->
                <div class="col-md-6">
                  <kendo-formfield>
                    <kendo-label
                      [for]="pentestStartDate"
                      text="Start Date *"
                    ></kendo-label>
                    <kendo-datepicker
                      formControlName="pentestStartDate"
                      #pentestStartDate
                      placeholder="Select start date"
                      class="w-100"
                      (valueChange)="reapplyValidation()"
                    ></kendo-datepicker>
                    <kendo-formhint>
                      <kendo-svg-icon
                        [icon]="infoCircleIcon"
                        class="me-1"
                      ></kendo-svg-icon>
                      Preferred start date for penetration testing
                    </kendo-formhint>
                    @if (
                      professionalServicesForm.get('pentestStartDate')?.invalid &&
                      professionalServicesForm.get('pentestStartDate')?.touched
                    ) {
                      <kendo-formerror>
                        <kendo-svg-icon
                          [icon]="exclamationTriangleIcon"
                          class="me-1"
                        ></kendo-svg-icon>
                        Start date is required
                      </kendo-formerror>
                    }
                  </kendo-formfield>
                </div>
              </div>
            </div>
          </div>
        }

        <!-- Attack Surface Assessment Configuration -->
        @if (showAttackSurfaceFields) {
          <div class="col-12 mt-4">
            <div class="config-section">
              <h5 class="section-title mb-3">
                <kendo-svg-icon
                  [icon]="shieldIcon"
                  class="me-2"
                ></kendo-svg-icon>
                Attack Surface Assessment Configuration
              </h5>
              <div class="row g-3">
                <!-- Number of IP Subnets -->
                <div class="col-md-6">
                  <kendo-formfield>
                    <kendo-label
                      [for]="ipSubnets"
                      text="Number of IP Subnets *"
                    ></kendo-label>
                    <kendo-numerictextbox
                      formControlName="ipSubnets"
                      #ipSubnets
                      [min]="1"
                      placeholder="Enter number of IP subnets"
                      class="w-100"
                      (valueChange)="reapplyValidation()"
                    ></kendo-numerictextbox>
                    <kendo-formhint>
                      <kendo-svg-icon
                        [icon]="infoCircleIcon"
                        class="me-1"
                      ></kendo-svg-icon>
                      Total number of IP subnets to be assessed
                    </kendo-formhint>
                    @if (
                      professionalServicesForm.get('ipSubnets')?.invalid &&
                      professionalServicesForm.get('ipSubnets')?.touched
                    ) {
                      <kendo-formerror>
                        <kendo-svg-icon
                          [icon]="exclamationTriangleIcon"
                          class="me-1"
                        ></kendo-svg-icon>
                        Number of IP subnets is required and must be at least 1
                      </kendo-formerror>
                    }
                  </kendo-formfield>
                </div>

                <!-- Start Date -->
                <div class="col-md-6">
                  <kendo-formfield>
                    <kendo-label
                      [for]="asaStartDate"
                      text="Start Date *"
                    ></kendo-label>
                    <kendo-datepicker
                      formControlName="asaStartDate"
                      #asaStartDate
                      placeholder="Select start date"
                      class="w-100"
                      (valueChange)="reapplyValidation()"
                    ></kendo-datepicker>
                    <kendo-formhint>
                      <kendo-svg-icon
                        [icon]="infoCircleIcon"
                        class="me-1"
                      ></kendo-svg-icon>
                      Preferred start date for attack surface assessment
                    </kendo-formhint>
                    @if (
                      professionalServicesForm.get('asaStartDate')?.invalid &&
                      professionalServicesForm.get('asaStartDate')?.touched
                    ) {
                      <kendo-formerror>
                        <kendo-svg-icon
                          [icon]="exclamationTriangleIcon"
                          class="me-1"
                        ></kendo-svg-icon>
                        Start date is required
                      </kendo-formerror>
                    }
                  </kendo-formfield>
                </div>
              </div>
            </div>
          </div>
        }

        <!-- Breach Attack Assessment Configuration -->
        @if (showBreachAttackFields) {
          <div class="col-12 mt-4">
            <div class="config-section">
              <h5 class="section-title mb-3">
                <kendo-svg-icon
                  [icon]="gearIcon"
                  class="me-2"
                ></kendo-svg-icon>
                Breach Attack Assessment Configuration
              </h5>
              <div class="row g-3">
                <!-- Assessment Package -->
                <div class="col-md-6">
                  <kendo-formfield>
                    <kendo-label
                      [for]="assessmentPackageSelect"
                      text="Assessment Package *"
                    ></kendo-label>
                    <kendo-dropdownlist
                      formControlName="assessmentPackage"
                      #assessmentPackageSelect
                      [data]="assessmentPackageOptions"
                      textField="text"
                      valueField="value"
                      placeholder="Select assessment package"
                      class="w-100"
                      (valueChange)="reapplyValidation()"
                    ></kendo-dropdownlist>
                    <kendo-formhint>
                      <kendo-svg-icon
                        [icon]="infoCircleIcon"
                        class="me-1"
                      ></kendo-svg-icon>
                      Choose the appropriate assessment package level
                    </kendo-formhint>
                    @if (
                      professionalServicesForm.get('assessmentPackage')?.invalid &&
                      professionalServicesForm.get('assessmentPackage')?.touched
                    ) {
                      <kendo-formerror>
                        <kendo-svg-icon
                          [icon]="exclamationTriangleIcon"
                          class="me-1"
                        ></kendo-svg-icon>
                        Assessment package selection is required
                      </kendo-formerror>
                    }
                  </kendo-formfield>
                </div>

                <!-- Start Date -->
                <div class="col-md-6">
                  <kendo-formfield>
                    <kendo-label
                      [for]="baaStartDate"
                      text="Start Date *"
                    ></kendo-label>
                    <kendo-datepicker
                      formControlName="baaStartDate"
                      #baaStartDate
                      placeholder="Select start date"
                      class="w-100"
                      (valueChange)="reapplyValidation()"
                    ></kendo-datepicker>
                    <kendo-formhint>
                      <kendo-svg-icon
                        [icon]="infoCircleIcon"
                        class="me-1"
                      ></kendo-svg-icon>
                      Preferred start date for breach attack assessment
                    </kendo-formhint>
                    @if (
                      professionalServicesForm.get('baaStartDate')?.invalid &&
                      professionalServicesForm.get('baaStartDate')?.touched
                    ) {
                      <kendo-formerror>
                        <kendo-svg-icon
                          [icon]="exclamationTriangleIcon"
                          class="me-1"
                        ></kendo-svg-icon>
                        Start date is required
                      </kendo-formerror>
                    }
                  </kendo-formfield>
                </div>
              </div>
            </div>
          </div>
        }

        @if (
          professionalServicesForm.get('selectedServices')?.invalid &&
          professionalServicesForm.get('selectedServices')?.touched
        ) {
          <div class="text-danger mt-2">
            <small>Please select at least one professional service</small>
          </div>
        }
      </div>
    </div>
  </form>
</div>
